/**
 * SubKrewe Terms & Conditions helpers.
 *
 * A SubKrewe may publish optional plain-text terms & conditions
 * (`subkrewe.terms_and_conditions`). Whenever a captain/admin edits the
 * terms, `subkrewe.terms_updated_at` is stamped (see stores/subkrewes.ts).
 *
 * Members record acceptances on their own document under
 * `member.subkrewe_terms_acceptances`, keyed by SubKrewe id. Each record
 * stores when the acceptance happened and a snapshot of
 * `terms_updated_at` at that time so we can detect when a SubKrewe has
 * published newer terms and force re-acceptance.
 */

import type { Member, SubKreweTermsAcceptance } from '~/shared/types/member.js'
import type { SubKrewe } from '~/shared/types/subkrewe.js'

/**
 * Coerce a Firestore timestamp-like value to a millisecond epoch, or
 * undefined when the value is missing / is still a sentinel (FieldValue).
 */
function toMillis(value: unknown): number | undefined {
  if (!value) return undefined
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'object' && value !== null) {
    const maybe = value as { toDate?: () => Date, seconds?: number }
    if (typeof maybe.toDate === 'function') {
      try {
        return maybe.toDate().getTime()
      }
      catch {
        // fall through
      }
    }
    if (typeof maybe.seconds === 'number') return maybe.seconds * 1000
  }
  return undefined
}

/**
 * Whether the given SubKrewe publishes non-empty T&Cs.
 */
export function subkreweHasTerms(subkrewe: SubKrewe | null | undefined): boolean {
  if (!subkrewe) return false
  const t = subkrewe.terms_and_conditions
  return typeof t === 'string' && t.trim().length > 0
}

/**
 * Return the acceptance record a member has for the given SubKrewe, if any.
 */
export function getAcceptance(
  member: Member | null | undefined,
  subkreweId: string | null | undefined,
): SubKreweTermsAcceptance | undefined {
  if (!member || !subkreweId) return undefined
  return member.subkrewe_terms_acceptances?.[subkreweId]
}

/**
 * Whether the member still needs to accept (or re-accept) the given
 * SubKrewe's T&Cs. Returns false if the SubKrewe has no terms.
 */
export function memberNeedsToAcceptTerms(
  member: Member | null | undefined,
  subkrewe: SubKrewe | null | undefined,
): boolean {
  if (!subkreweHasTerms(subkrewe)) return false
  if (!member || !subkrewe) return false

  const acceptance = getAcceptance(member, subkrewe.id)
  if (!acceptance) return true

  const subkreweTs = toMillis(subkrewe.terms_updated_at)
  const acceptedTs = toMillis(acceptance.terms_updated_at)

  // If the SubKrewe has a newer terms_updated_at than the version the
  // member previously accepted, re-acceptance is required. When either
  // timestamp is missing we err on the side of accepting what we have
  // (legacy records predate this field).
  if (subkreweTs !== undefined && acceptedTs !== undefined && subkreweTs > acceptedTs) {
    return true
  }

  return false
}
